import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ChevronLeft, LogOut } from 'lucide-react';
import { cn } from '@/utils/cn';
import { NAV_ITEMS, PAGE_TITLES } from '@/constants';
import { ParticleBackground } from '@/components/ui/particle-background';
import { getUserInfo, logout } from '@/utils/auth';
import logoSrc from '@/assets/logo.png';

export function AppLayout(): JSX.Element {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const user = getUserInfo();

  return (
    <div className="relative flex h-screen overflow-hidden tech-grid">
      {/* 主页面背景：home_page.png 游戏主题装饰图（四角装饰、中央留白）。
          必须放在 AppLayout 根层级 z-0（aside/main 均为 z-10 在其上）——若放在页面内 fixed 全视口会盖住侧栏（2026-08-26 实测教训）；fixed 不随滚动。
          用户要求（2026-08-26）：创作中心/试玩列表/创意工厂/资产库全部主页面统一铺底图 */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/home_page.png)' }} />
        {/* 深灰蒙层压暗底图（2026-08-26 用户要求：原图颜色太显眼，干扰内容阅读） */}
        <div className="absolute inset-0 bg-slate-950/45" />
      </div>
      {/* Particle background */}
      <ParticleBackground count={50} className="opacity-40" />

      {/* Fixed sidebar */}
      <aside
        className={cn(
          'sidebar-edge relative z-10 flex h-screen shrink-0 flex-col gap-1.5 border-r border-border/50 p-4 transition-all duration-300',
          'bg-[hsl(var(--card)/0.5)] backdrop-blur-xl',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        {/* Logo：静态展示（用户要求 2026-08-26 去掉呼吸/变色动画：logo-glow 光晕已删，logo-breathe 动画以行内 animation:none 覆盖） */}
        <div className={cn('flex items-center gap-3 pb-4', collapsed ? 'justify-center px-0' : 'px-3')}>
          <div className="logo-wrap relative flex h-11 w-11 shrink-0 items-center justify-center">
            <img
              src={logoSrc}
              alt="Playgen Logo"
              className="logo-float relative h-full w-full object-contain"
              style={{ animation: 'none' }}
            />
          </div>
          {!collapsed && (
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              {/* 卡通艺术字 LOGO（透明底横版），替代原彩虹渐变文字标题 */}
              <img src="/cygf.png" alt="创意工坊" className="brand-img h-auto w-full object-contain" style={{ animation: 'none' }} />
              <span className="logo-sub text-xs tracking-[0.28em]">Playgen</span>
            </div>
          )}
        </div>
        {/* 品牌区与导航间的极光 hairline 分隔 */}
        {!collapsed && (
          <div aria-hidden className="mx-3 mb-3 h-px bg-gradient-to-r from-neon-cyan/50 via-neon-purple/40 to-transparent" />
        )}

        {/* Nav items */}
        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  'nav-glow group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] transition-all duration-300',
                  isActive
                    ? 'bg-[hsl(var(--primary)/0.15)] text-foreground font-medium shadow-sm shadow-neon-cyan/5'
                    : 'text-muted-foreground hover:bg-[hsl(var(--muted)/0.7)] hover:text-foreground',
                  collapsed && 'justify-center px-0',
                )}
                title={collapsed ? item.label : undefined}
              >
                <span
                  className={cn(
                    'shrink-0 text-base transition-transform duration-300 group-hover:scale-110',
                    item.accent,
                    isActive && item.glow,
                  )}
                >
                  <item.icon size={18} />
                </span>
                {!collapsed && <span>{item.label}</span>}
                {isActive && !collapsed && (
                  <div className={cn('ml-auto h-1.5 w-1.5 rounded-full', item.dot)} />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="mt-auto space-y-3 border-t border-border/30 pt-4">
          {/* User info */}
          {user && (
            <div
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-2 py-1.5',
                collapsed && 'justify-center px-0',
              )}
              title={user.name}
            >
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-border/60"
                />
              ) : (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary)/0.2)] text-xs font-bold text-foreground ring-1 ring-border/60">
                  {user.name?.charAt(0) ?? 'U'}
                </div>
              )}
              {!collapsed && (
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-xs font-medium text-foreground">{user.name}</span>
                  <span className="truncate text-[10px] text-muted-foreground">
                    {user.email || '飞书用户'}
                  </span>
                </div>
              )}
              {!collapsed && (
                <button
                  onClick={logout}
                  className="ml-auto rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-[hsl(var(--muted)/0.5)] hover:text-red-400"
                  title="退出登录"
                >
                  <LogOut size={14} />
                </button>
              )}
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground hover:bg-[hsl(var(--muted)/0.5)]',
              collapsed && 'justify-center',
            )}
          >
            <ChevronLeft
              size={14}
              className={cn('shrink-0 transition-transform duration-300', collapsed && 'rotate-180')}
            />
            {!collapsed && '收起'}
          </button>
        </div>
      </aside>

      {/* Scrollable content */}
      <main className="relative z-10 flex flex-1 flex-col overflow-hidden">
        {/* 顶部极光扫描 hairline（缓慢流动，科技感） */}
        <div aria-hidden className="main-scanline shrink-0" />
        {/* min-h-0 让内页可以在 flex 布局中收缩到视口高度；否则组件图鉴等双栏页面会把内容高度传给外层，出现双重滚动或右栏不能独立滚动。 */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {/* pathname 作 key：一二级路由切换时重挂载触发 route-enter 过渡动画 */}
          <div key={pathname} className="route-enter h-full min-h-0">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
